/**
 * The product Studio's preview renders, as it crosses the platform -> app bridge
 * (VRG-827).
 *
 * ## Why this is a shared type and not duplicated per app
 *
 * The platform SENDS it and `apps/app` READS it, over `postMessage` between two
 * separately-built bundles. Nothing checks the two ends agree — a renamed field
 * arrives `undefined` and renders as a blank line, with no error anywhere. That is
 * the shape working-conventions §76 describes for the cross-repo website contract,
 * and the same argument applies across a bundle boundary inside one repo.
 *
 * Both apps already import `@repo/types` (`STUDIO_THEME_DEFAULTS` is next door), so
 * one declaration costs nothing and makes a mismatch a compile error on whichever
 * side drifts first.
 *
 * ## DISPLAY FIELDS ONLY
 *
 * Studio is a theming surface. It needs what paints — a title, an image, a
 * description — and must never carry pricing, inventory, owner or certificate data
 * into a preview that is, by construction, showing fabricated provenance around it.
 * `id` is here to key the React query, not to look anything up.
 *
 * Every field optional because the SOURCE is optional: a brand's product may have no
 * image, no description and no SKU, and the merge treats an absent field as "keep the
 * mock's" rather than "render a blank".
 */
export interface StudioPreviewProduct {
  /** The real product's id. Used as a cache key, never dereferenced by the app. */
  id: string;
  title?: string;
  sku?: string | null;
  /** Ordered, as the product's own gallery orders them. */
  images?: string[] | null;
  description?: string | null;
  details?: string | null;
  /** The product's category, e.g. `bags`. Drives category-conditional copy. */
  type?: string | null;
}

/** The `postMessage` type tag. One string, so neither end can typo it apart. */
export const STUDIO_PREVIEW_PRODUCT_MESSAGE = 'studio-product' as const;
